import { AutomationStatusBadge } from "~/components/AutomationStatusBadge"
import { Item } from "~/components/ui/item"
import { Field, FieldLabel } from "~/components/ui/field"
import { useParams } from "react-router-dom"
import { Link } from "~/components/Link"
import { PlayIcon, SparklesIcon } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { AppHeader } from "~/components/AppHeader"
import { EmptyState, LoadingState } from "~/components/screens/StateScreen"
import { EntityIdentity } from "~/components/EntityIdentity"

import { Button } from "~/components/ui/button"
import { Card } from "~/components/ui/card"
import { Input } from "~/components/ui/input"

import { ScrollArea } from "~/components/ui/scroll-area"
import { useTeam } from "~/hooks/useTeam"

import { api } from "~/trpc/react"

export default function AutomationRunsPage() {
	const { team, isLoading, orgSlug } = useTeam()
	const { automationId } = useParams<{ automationId: string }>()
	const [manualIssueId, setManualIssueId] = useState("")
	const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
	const ruleQuery = api.automations.get.useQuery(
		{ teamId: team?.id ?? "", ruleId: automationId ?? "" },
		{
			enabled: Boolean(
				team?.id && team.automationsFeatureEnabled && automationId
			)
		}
	)
	const runsQuery = api.automations.runs.useQuery(
		{ teamId: team?.id ?? "", ruleId: automationId, limit: 50 },
		{
			enabled: Boolean(
				team?.id && team.automationsFeatureEnabled && automationId
			)
		}
	)
	const runDetailsQuery = api.automations.runDetails.useQuery(
		{ teamId: team?.id ?? "", runId: selectedRunId ?? "" },
		{
			enabled: Boolean(
				team?.id && team.automationsFeatureEnabled && selectedRunId
			)
		}
	)
	const previewMutation = api.automations.preview.useMutation()
	const manualRunMutation = api.automations.manualRun.useMutation()

	if (isLoading) return <LoadingState />

	if (!team || !automationId) {
		return <EmptyState title="Automation not found." />
	}

	if (!team.automationsFeatureEnabled) {
		return <EmptyState title="Automations unavailable" />
	}

	if (ruleQuery.isLoading) return <LoadingState />

	const editorHref = `/${orgSlug}/${team.slug}/automations/${automationId}`

	return (
		<div className="flex h-full min-h-0 flex-col text-text-main">
			<AppHeader
				contentClassName="flex items-center gap-2"
				actions={
					<Button asChild variant="outline" size="sm">
						<Link to={editorHref}>Edit rule</Link>
					</Button>
				}
			>
				<EntityIdentity kind="team" name={team.name} image={team.logo} />
				<span className="text-text-sub">/</span>
				<Link
					to={`/${orgSlug}/${team.slug}/automations`}
					className="font-medium text-text-main hover:underline"
				>
					Automations
				</Link>
				<span className="text-text-sub">/</span>
				<span className="truncate font-medium text-text-main">
					{ruleQuery.data?.name ?? "Automation"} · Runs
				</span>
			</AppHeader>

			<main className="min-h-0 flex-1 overflow-hidden p-5">
				<div className="mx-auto grid h-full w-full max-w-6xl gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
					<div className="space-y-5">
						<Card density="compact">
							<h2 className="font-semibold">Manual check</h2>
							<Field>
								<FieldLabel>Issue id</FieldLabel>
								<Input
									placeholder="Optional"
									value={manualIssueId}
									onChange={(event) => setManualIssueId(event.target.value)}
								/>
							</Field>
							<div className="grid grid-cols-2 gap-2">
								<Button
									type="button"
									variant="outline"
									disabled={previewMutation.isPending}
									onClick={async () => {
										const result = await previewMutation.mutateAsync({
											teamId: team.id,
											ruleId: automationId,
											issueId: manualIssueId || null
										})
										await runsQuery.refetch()
										toast.success(
											result.runId ? "Preview recorded" : "Preview skipped"
										)
									}}
								>
									<SparklesIcon className="size-4" /> Preview
								</Button>
								<Button
									type="button"
									disabled={manualRunMutation.isPending}
									onClick={async () => {
										await manualRunMutation.mutateAsync({
											teamId: team.id,
											ruleId: automationId,
											issueId: manualIssueId || null
										})
										await runsQuery.refetch()
										toast.success("Manual run queued")
									}}
								>
									<PlayIcon className="size-4" /> Run
								</Button>
							</div>
						</Card>

						{runDetailsQuery.data ? (
							<Card density="compact">
								<h2 className="font-semibold">Run details</h2>
								<Card
									variant="secondary"
									className="text-xs text-text-sub"
									density="tight"
								>
									<p>
										Evaluation:{" "}
										{JSON.stringify(runDetailsQuery.data.evaluation ?? {})}
									</p>
									<p>
										Trigger:{" "}
										{JSON.stringify(runDetailsQuery.data.triggerSnapshot ?? {})}
									</p>
								</Card>
								<div className="space-y-2">
									{runDetailsQuery.data.actions.map((action) => (
										<Card
											key={action.id}
											variant="secondary"
											className="text-sm"
											density="tight"
										>
											<div className="flex items-center justify-between gap-2">
												<span>
													{action.branch} · {action.actionType}
												</span>
												<AutomationStatusBadge status={action.status} />
											</div>
											{action.error ? (
												<p className="mt-1 text-xs text-destructive">
													{action.error}
												</p>
											) : null}
											{action.deliveries.map((delivery) => (
												<p
													key={delivery.id}
													className="mt-1 text-xs text-text-sub"
												>
													{delivery.provider} delivery · {delivery.status}
													{delivery.error ? ` · ${delivery.error}` : ""}
												</p>
											))}
										</Card>
									))}
								</div>
							</Card>
						) : null}
					</div>

					<Card className="min-h-0 overflow-hidden" density="flush">
						<div className="border-b border-border p-4">
							<h2 className="font-semibold">Recent runs</h2>
						</div>
						<ScrollArea className="min-h-0 flex-1">
							<div className="space-y-2 p-4">
								{runsQuery.data?.map((run) => (
									<Item
										asChild
										variant="interactive"
										size="sm"
										key={run.id}
										className="flex-col items-stretch"
									>
										<button
											type="button"

											onClick={() => setSelectedRunId(run.id)}
											className="w-full text-left"
											aria-pressed={selectedRunId === run.id}
										>
											<div className="flex items-center justify-between gap-3">
												<span className="truncate font-mono text-xs text-text-sub">
													{run.id}
												</span>
												<AutomationStatusBadge status={run.status} />
											</div>
											<p className="mt-1 text-xs text-text-sub">
												{run.kind} · {run.triggerType}
												{run.lastError ? ` · ${run.lastError}` : ""}
											</p>
										</button>
									</Item>
								))}
								{!runsQuery.data?.length ? (
									<p className="text-sm text-text-sub">No runs yet.</p>
								) : null}
							</div>
						</ScrollArea>
					</Card>
				</div>
			</main>
		</div>
	)
}
